/** Lead list with filters, totals row and Excel export (§4.4). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, Plus, Search } from 'lucide-react';
import { api, getAccessToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { peso, percent, shortDate } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  filterClass,
} from '../../components/ui';

interface LeadRow {
  id: string;
  code: string;
  projectName: string;
  amountVatEx: string;
  grossProfit?: string;
  netProfit?: string;
  netMarginPct?: string;
  weightedAmount: string;
  expectedCloseDate: string | null;
  locked: boolean;
  customer: { name: string; code: string };
  stage: { label: string; code: string };
  owner: { name: string };
  solutionCategory: { label: string } | null;
  salesOrder: { code: string } | null;
}

interface Totals {
  amountVatEx: string;
  grossProfit?: string;
  netProfit?: string;
  weightedAmount: string;
}

interface ParameterGroup {
  key: string;
  values: { id: string; code: string; label: string }[];
}

const stageTone = (code: string): 'ok' | 'neutral' | 'info' | 'warning' => {
  if (code === 'CLOSED_WON') return 'ok';
  if (code === 'CLOSED_LOST') return 'neutral';
  if (code === 'ON_HOLD') return 'warning';
  return 'info';
};

export function LeadsPage(): JSX.Element {
  const { can } = useAuth();
  const [filters, setFilters] = useState({ q: '', stageId: '', solutionCategoryId: '', openOnly: '' });
  const [page, setPage] = useState(1);

  const parameters = useQuery({
    queryKey: ['parameters'],
    queryFn: () => api<{ data: ParameterGroup[] }>('/parameters'),
  });

  const params = new URLSearchParams({ page: String(page), pageSize: '25' });
  if (filters.q) params.set('q', filters.q);
  if (filters.stageId) params.set('stageId', filters.stageId);
  if (filters.solutionCategoryId) params.set('solutionCategoryId', filters.solutionCategoryId);
  if (filters.openOnly) params.set('openOnly', 'true');

  const leads = useQuery({
    queryKey: ['leads', params.toString()],
    queryFn: () =>
      api<{ data: LeadRow[]; total: number; pageSize: number; totals: Totals }>(
        `/leads?${params.toString()}`,
      ),
  });

  const stages = parameters.data?.data.find((g) => g.key === 'lead_stage')?.values ?? [];
  const solutions = parameters.data?.data.find((g) => g.key === 'solution_category')?.values ?? [];
  const showMoney = can('leads.view_financials');
  const total = leads.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / (leads.data?.pageSize ?? 25)));

  const exportExcel = async (): Promise<void> => {
    const response = await fetch(`/api/v1/leads/export?${params.toString()}`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ITCORE-SF_Leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Leads</h1>
          <p className="text-sm text-slate-500">
            Proposal summaries with their margins. The detailed BOM stays in Google Drive.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => void exportExcel()}>
            <Download aria-hidden="true" className="h-4 w-4" /> Export
          </Button>
          {can('leads.create') && (
            <Link to="/leads/new">
              <Button>
                <Plus aria-hidden="true" className="h-4 w-4" /> New lead
              </Button>
            </Link>
          )}
        </div>
      </header>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${filterClass} w-full pl-8`}
              placeholder="Search lead, customer, project or proposal reference"
              value={filters.q}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, q: event.target.value });
              }}
            />
          </div>
          <select
            className={`${filterClass} flex-1 sm:flex-none sm:w-36`}
            value={filters.stageId}
            onChange={(event) => {
              setPage(1);
              setFilters({ ...filters, stageId: event.target.value });
            }}
          >
            <option value="">All stages</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.label}
              </option>
            ))}
          </select>
          <select
            className={`${filterClass} flex-1 sm:flex-none sm:w-40`}
            value={filters.solutionCategoryId}
            onChange={(event) => {
              setPage(1);
              setFilters({ ...filters, solutionCategoryId: event.target.value });
            }}
          >
            <option value="">All solutions</option>
            {solutions.map((solution) => (
              <option key={solution.id} value={solution.id}>
                {solution.label}
              </option>
            ))}
          </select>
          <label className="flex shrink-0 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
              checked={filters.openOnly === 'true'}
              onChange={(event) => {
                setPage(1);
                setFilters({ ...filters, openOnly: event.target.checked ? 'true' : '' });
              }}
            />
            Open only
          </label>
        </div>

        {leads.isLoading && <Skeleton className="h-64 w-full" />}

        {leads.data?.data.length === 0 && (
          <EmptyState
            title="No leads yet"
            description="Create the first lead to record a proposal and its margin."
            action={
              can('leads.create') ? (
                <Link to="/leads/new">
                  <Button>
                    <Plus aria-hidden="true" className="h-4 w-4" /> New lead
                  </Button>
                </Link>
              ) : undefined
            }
          />
        )}

        {(leads.data?.data.length ?? 0) > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>Lead</Th>
                  <Th>Customer</Th>
                  <Th>Stage</Th>
                  <Th align="right">Amount (VAT-ex)</Th>
                  {showMoney && <Th align="right">Gross profit</Th>}
                  {showMoney && <Th align="right">Net profit</Th>}
                  <Th align="right">Weighted</Th>
                  <Th>Owner</Th>
                  <Th>Expected close</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {leads.data?.data.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td>
                      <Link
                        to={`/leads/${row.id}`}
                        className="font-medium text-navy hover:underline dark:text-white"
                      >
                        {row.projectName}
                      </Link>
                      <p className="font-mono text-xs text-slate-500">
                        {row.code}
                        {row.salesOrder ? ` → ${row.salesOrder.code}` : ''}
                      </p>
                    </Td>
                    <Td>{row.customer.name}</Td>
                    <Td>
                      <StatusChip tone={stageTone(row.stage.code)}>{row.stage.label}</StatusChip>
                    </Td>
                    <Td align="right" className="tabular">
                      {peso(row.amountVatEx)}
                    </Td>
                    {showMoney && (
                      <Td align="right" className="tabular">
                        {peso(row.grossProfit)}
                      </Td>
                    )}
                    {showMoney && (
                      <Td align="right" className="tabular">
                        <span
                          className={
                            row.netProfit !== undefined && Number(row.netProfit) < 0
                              ? 'font-semibold text-red-600'
                              : ''
                          }
                        >
                          {peso(row.netProfit)}
                        </span>
                        {row.netMarginPct !== undefined && (
                          <span className="block text-xs text-slate-500">
                            {percent(row.netMarginPct)}
                          </span>
                        )}
                      </Td>
                    )}
                    <Td align="right" className="tabular">
                      {peso(row.weightedAmount)}
                    </Td>
                    <Td>{row.owner.name}</Td>
                    <Td>{shortDate(row.expectedCloseDate)}</Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold dark:border-slate-700">
                  <Td>Totals</Td>
                  <Td>{''}</Td>
                  <Td>{''}</Td>
                  <Td align="right" className="tabular">
                    {peso(leads.data?.totals.amountVatEx)}
                  </Td>
                  {showMoney && (
                    <Td align="right" className="tabular">
                      {peso(leads.data?.totals.grossProfit)}
                    </Td>
                  )}
                  {showMoney && (
                    <Td align="right" className="tabular">
                      {peso(leads.data?.totals.netProfit)}
                    </Td>
                  )}
                  <Td align="right" className="tabular">
                    {peso(leads.data?.totals.weightedAmount)}
                  </Td>
                  <Td>{''}</Td>
                  <Td>{''}</Td>
                </tr>
              </tfoot>
            </TableWrap>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                {total} lead{total === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </Button>
                <span className="self-center">
                  {page} / {lastPage}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= lastPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
